if (!customElements.get('dt-testimonial-motion')) {
  customElements.define('dt-testimonial-motion', class extends HTMLElement {
    async connectedCallback() {
      this.abort = new AbortController();
      const { signal } = this.abort;
      await customElements.whenDefined('slider-component');
      if (signal.aborted) return;
      this.carousel = this.querySelector('slider-component');
      this.track = this.querySelector('.dt-testimonial-grid');
      if (!this.track || this.dataset.autoplay !== 'true') return;
      this.cards = [...this.track.children];
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.motion.matches || Boolean(window.Shopify?.designMode);
      this.visible = false;
      this.hovered = false;
      this.focused = false;
      this.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') this.hovered = true;
      }, { signal });
      this.addEventListener('pointerleave', () => { this.hovered = false; }, { signal });
      this.addEventListener('focusin', () => { this.focused = true; }, { signal });
      this.addEventListener('focusout', (event) => {
        this.focused = this.contains(event.relatedTarget);
      }, { signal });
      const takeControl = () => {
        this.paused = true;
        this.configure();
      };
      this.track.addEventListener('pointerdown', takeControl, { signal, passive: true });
      this.track.addEventListener('wheel', takeControl, { signal, passive: true });
      this.track.addEventListener('keydown', takeControl, { signal });
      this.carousel.addEventListener('click', (event) => {
        if (event.target.closest('.slider-button')) takeControl();
      }, { signal, capture: true });
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) this.paused = true;
        this.configure();
      }, { signal });
      this.observer = new IntersectionObserver(([entry]) => { this.visible = entry.isIntersecting; });
      this.observer.observe(this);
      this.resize = new ResizeObserver(() => this.configure());
      this.resize.observe(this.track);
      this.configure();
      const tick = (time) => {
        const elapsed = Math.min(time - (this.lastTime || time), 50);
        this.lastTime = time;
        if (this.cycle && this.visible && !document.hidden && !this.hovered && !this.focused) {
          // Decreasing the scroll offset moves the screenshots visually to the right.
          this.position -= elapsed * 0.018;
          if (this.position < 0) this.position += this.cycle;
          this.track.scrollLeft = this.position;
        }
        this.frame = requestAnimationFrame(tick);
      };
      this.frame = requestAnimationFrame(tick);
    }

    configure() {
      this.removeLoop();
      const overflow = this.track.scrollWidth > this.track.clientWidth + 1;
      if (!overflow || this.motion.matches || this.paused) {
        return;
      }
      this.clones = this.cards.map((card) => {
        const clone = card.cloneNode(true);
        clone.removeAttribute('id');
        clone.removeAttribute('data-shopify-editor-block');
        clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('inert', '');
        this.track.append(clone);
        return clone;
      });
      this.track.setAttribute('data-looping', '');
      this.setAttribute('data-running', '');
      this.cycle = this.clones[0].offsetLeft - this.cards[0].offsetLeft;
      this.position = this.track.scrollLeft || this.cycle;
      this.track.scrollLeft = this.position;
      this.carousel.enableSliderLooping = true;
      this.carousel.querySelectorAll('.slider-button').forEach((button) => { button.disabled = false; });
    }

    removeLoop() {
      if (!this.cycle) return;
      const position = this.track.scrollLeft % this.cycle;
      this.clones.forEach((clone) => clone.remove());
      this.clones = [];
      this.cycle = 0;
      this.track.removeAttribute('data-looping');
      this.removeAttribute('data-running');
      this.track.scrollLeft = position;
      this.carousel.enableSliderLooping = false;
      this.carousel.resetPages();
    }

    disconnectedCallback() {
      this.abort?.abort();
      cancelAnimationFrame(this.frame);
      this.observer?.disconnect();
      this.resize?.disconnect();
      this.removeLoop();
    }
  });
}
